import type { ActiveEventBasedMyBetPart, MyBetPart } from '@ps/isp-web-services-types';
import classNames from 'clsx';
import React from 'react';

import BetDate from '@swf/sports/components/BetDate/BetDate';
import EventStatus from '@swf/sports/components/EventStatus/EventStatus';
import useEventStatus from '@swf/sports/components/EventStatus/hooks/useEventStatus';
import type { EventStatus as EventStatusType } from '@swf/sports/components/EventStatus/hooks/useEventStatus';
import Scoreboard from '@swf/sports/components/Scoreboard/Scoreboard';
import useParticipants from '@swf/sports/hooks/useParticipants';
import { useLiveData } from '@swf/sports/providers/LiveDataProvider';
import { getIsScoreboardSupportedMarketLevel } from '@swf/sports/utils/getIsScoreboardSupportedMarketLevel';
import useIsEventTypeLiveDataEnabled from '@swf/sports/utils/useIsEventTypeLiveDataEnabled';

import styles from './BetEventStatus.scss';

const betDateEventStatuses: EventStatusType[] = ['SCHEDULED', 'COMPLETE', 'SCHEDULED_NO_IN_PLAY'];

interface Props {
  part: MyBetPart;
  isSettled?: boolean;
}

function isActiveEventBasedMyBetPart(part: MyBetPart): part is ActiveEventBasedMyBetPart {
  return 'enrichedParticipants' in part;
}

const BetEventStatus: React.FC<Props> = ({ part, isSettled }) => {
  const { isError } = useLiveData();
  const eventStatus = useEventStatus({
    inPlay: part.inPlay,
    canTurnInPlay: true,
    startTime: part.startTime || ''
  });

  const enrichedParticipants = isActiveEventBasedMyBetPart(part)
    ? part.enrichedParticipants
    : undefined;
  const fetchedParticipants = useParticipants(part.eventDescription);
  const participants = enrichedParticipants || fetchedParticipants;

  const eventTypeId = Number(part.sportId);
  const eventId = Number(part.eventId);

  const isLiveDataEnabled = useIsEventTypeLiveDataEnabled(eventTypeId);

  const showBetDate = betDateEventStatuses.includes(eventStatus) || isSettled || !isLiveDataEnabled;

  const showScoreboard =
    !isError &&
    !isSettled &&
    part.inPlay &&
    isLiveDataEnabled &&
    getIsScoreboardSupportedMarketLevel(part.marketLevels) &&
    part.eventId;

  return (
    <div data-testid="scoreboard" className={styles.scoreboardContainer}>
      {showScoreboard && (
        <Scoreboard
          enrichedParticipants={participants}
          eventId={eventId}
          eventTypeId={eventTypeId}
        />
      )}

      {part.startTime &&
        (showBetDate ? (
          <span className={styles.eventStartDate}>
            <BetDate eventStartDate={part.startTime || ''} weekdayFormat="long" />
          </span>
        ) : (
          <span
            className={classNames(styles.status, {
              [styles.scoreboard]: showScoreboard
            })}
          >
            <EventStatus
              eventTypeId={eventTypeId}
              isInPlay={part.inPlay}
              isTurnInPlay
              startTime={part.startTime}
              size="XSmall"
              eventId={eventId}
              marketLevels={part.marketLevels}
            />
          </span>
        ))}
    </div>
  );
};

export default BetEventStatus;
